'use client';

import { c as _c } from "react/compiler-runtime";
import { Sparkles } from 'lucide-react';
import { Button, Icon, Input, SearchModal as SearchModalPrimitive } from '~/components/primitives';
import { usePortalCtx } from '~/features/global/portal';
import type { SearchModalProps } from './index';
import type { Variants } from 'motion/react';
function wordCount(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}
const SPOTLIGHT_VARIANTS: Variants = {
  initial: {
    opacity: 0,
    y: -20
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.2,
      ease: 'easeOut'
    }
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: {
      duration: 0.15,
      ease: 'easeIn'
    }
  }
};
export const ActivitySearchModal: React.FC<SearchModalProps> = props => {
  const $ = _c(61);
  const portal = usePortalCtx();
  const wc = wordCount(props.searchString.value);
  const showAiShortcut = portal.portal !== "BIZZ" && wc > 2 && !!props.onAiSearchSubmit;
  const t0 = props.onModalClose;
  const t1 = props.onModalClose;
  let t2;
  if ($[0] !== props.i18n) {
    t2 = props.i18n._("Close");
    $[0] = props.i18n;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Icon name="close" size="lg" />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== props.onModalClose || $[4] !== t2) {
    t4 = <Button variant="opaque" type="button" onClick={t1} aria-label={t2} className="fixed right-8 top-8 z-[1] hidden rounded-full p-2 lg:flex">{t3}</Button>;
    $[3] = props.onModalClose;
    $[4] = t2;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== props.i18n) {
    t5 = props.i18n._("Search activities");
    $[6] = props.i18n;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== props.searchString) {
    t6 = e => props.searchString.set(e.currentTarget.value);
    $[8] = props.searchString;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== props) {
    t7 = e_0 => {
      if (e_0.key === "Enter" && !e_0.nativeEvent.isComposing) {
        props.onSubmit();
      }
    };
    $[10] = props;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Icon name="search" className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-400" size="lg" />;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== props) {
    t9 = props.searchString.value ? <button type="button" onClick={() => props.onClear()} className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full p-1 text-blue-400 transition-colors hover:text-primary"><Icon name="close" size="xs" /></button> : undefined;
    $[13] = props;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== props.searchString.value || $[16] !== t5 || $[17] !== t6 || $[18] !== t7 || $[19] !== t9) {
    t10 = <Input autoFocus={true} type="search" placeholder={t5} value={props.searchString.value} onChange={t6} onKeyDown={t7} className="ring-0! focus-within:ring-0! hover:ring-0! h-[60px] flex-1 [&_input::-webkit-search-cancel-button]:hidden [&_input::-webkit-search-decoration]:hidden [&_input]:ml-4 [&_input]:appearance-none" iconBefore={t8} iconAfter={t9} />;
    $[15] = props.searchString.value;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const t11 = props.onSubmit;
  let t12;
  if ($[21] !== props.i18n) {
    t12 = props.i18n._("Show results");
    $[21] = props.i18n;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== props.onSubmit || $[24] !== t12) {
    t13 = <Button className="hidden flex-1 rounded-full px-4 sm:flex sm:flex-none sm:px-5" onClick={t11} variant="tertiary">{t12}</Button>;
    $[23] = props.onSubmit;
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== props.i18n || $[27] !== props.onAiSearchSubmit || $[28] !== showAiShortcut) {
    t14 = showAiShortcut ? <Button type="button" className="hidden flex-1 items-center justify-center gap-2 rounded-full px-4 sm:flex sm:flex-none sm:px-5" onClick={props.onAiSearchSubmit} variant="secondary"><Sparkles className="size-4 shrink-0" strokeWidth={2.25} aria-hidden={true} />{props.i18n._("activity.search.cmdk.aiSearch", undefined, {
        message: "AI search"
      })}</Button> : null;
    $[26] = props.i18n;
    $[27] = props.onAiSearchSubmit;
    $[28] = showAiShortcut;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t13 || $[31] !== t14) {
    t15 = <div className="flex min-h-[60px] gap-2 sm:shrink-0">{t13}{t14}</div>;
    $[30] = t13;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t10 || $[34] !== t15) {
    t16 = <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">{t10}{t15}</div>;
    $[33] = t10;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  const t17 = props.onSubmit;
  let t18;
  if ($[36] !== props.i18n) {
    t18 = props.i18n._("Show results");
    $[36] = props.i18n;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== props.onSubmit || $[39] !== t18) {
    t19 = <Button className="w-full rounded-full" onClick={t17} variant="tertiary">{t18}</Button>;
    $[38] = props.onSubmit;
    $[39] = t18;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== props.i18n || $[42] !== props.onAiSearchSubmit || $[43] !== showAiShortcut) {
    t20 = showAiShortcut ? <Button type="button" className="w-full rounded-full" onClick={props.onAiSearchSubmit} variant="secondary"><span className="inline-flex items-center justify-center gap-2"><Sparkles className="size-4 shrink-0" strokeWidth={2.25} aria-hidden={true} />{props.i18n._("activity.search.cmdk.aiSearch", undefined, {
          message: "AI search"
        })}</span></Button> : null;
    $[41] = props.i18n;
    $[42] = props.onAiSearchSubmit;
    $[43] = showAiShortcut;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== t19 || $[46] !== t20) {
    t21 = <div className="flex flex-col gap-2 sm:hidden">{t19}{t20}</div>;
    $[45] = t19;
    $[46] = t20;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  const t22 = props.onModalClose;
  let t23;
  if ($[48] !== props.i18n) {
    t23 = props.i18n._("Close");
    $[48] = props.i18n;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== props.onModalClose || $[51] !== t23) {
    t24 = <Button variant="opaque" type="button" onClick={t22} className="fixed bottom-8 left-1/2 mx-auto -translate-x-1/2 lg:hidden">{t23}</Button>;
    $[50] = props.onModalClose;
    $[51] = t23;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] !== t16 || $[54] !== t21 || $[55] !== t24 || $[56] !== t4) {
    t25 = <div className="relative flex flex-col gap-3 p-8 sm:p-0">{t4}{t16}{t21}{t24}</div>;
    $[53] = t16;
    $[54] = t21;
    $[55] = t24;
    $[56] = t4;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  if ($[58] !== props.onModalClose || $[59] !== t25) {
    t26 = <SearchModalPrimitive onClose={t0} backgroundClassName="bg-black/10 backdrop-blur-none" variants={SPOTLIGHT_VARIANTS} className="my-0! sm:mt-8! sm:mb-0! h-auto! rounded-xl! max-w-2xl" modalClassName="inset-0 z-10">{t25}</SearchModalPrimitive>;
    $[58] = props.onModalClose;
    $[59] = t25;
    $[60] = t26;
  } else {
    t26 = $[60];
  }
  return t26;
};